import { useState } from "react";
import { Link } from "react-router-dom";
import { FadeUp } from "./Reveal";
import { NoteEyebrow } from "./NoteEyebrow";
import { EmSwash } from "./EmSwash";
import { SampleCampaignPreview } from "./SampleCampaignPreview";
import { STEPS } from "../data/content";

/**
 * HowItWorks — compact band (founder pick: layout B, no animation).
 * Split header, then two horizontal step rows with the sample phone alongside. One handwritten note (the eyebrow); palette dashes
 * mark step progress; the IRS earmark sentence is a footnote under donors.
 * Phones: a Students / Donors toggle shows one row at a time.
 */
export const DONOR_STEPS = [
  {
    no: "01",
    title: "Give.",
    body: "Your gift goes to My Rich Aunt, a registered 501(c)(3). Fully tax-deductible, receipt right away.",
  },
  {
    no: "02",
    title: "Choose a story that moves you.",
    body: "Tell us which student you'd like to support, or give to the general fund. Either way, your gift is made to My Rich Aunt, and it is what lets us award grants to verified students.",
    footnote: true,
  },
  {
    no: "03",
    title: "Capped for fairness.",
    body: "During beta, campaigns are capped at $500, so your gift stretches across a community of students. The cap rises with the pilot.",
  },
];

export const FOOTNOTE = "The tax rule behind this: a gift to a 501(c)(3) is deductible because the charity, not the donor, decides who receives the grant. Your preference is welcome and it matters; the decision stays with My Rich Aunt.";

export const TRACK = ["bg-tangerine", "bg-cobalt", "bg-grass"];

export const StepTrack = ({ index }) => (
  <span aria-hidden="true" className="flex items-center gap-1.5" data-testid="how-step-track">
    {TRACK.map((c, i) => (
      <span key={c} className={`h-2 w-7 rounded-full ${i <= index ? c : "bg-ink/10"}`} />
    ))}
  </span>
);

const Row = ({ label, accent, steps, footnote, testid }) => (
  <div data-testid={testid}>
    <div className="hidden items-center gap-4 lg:flex">
      <p className="inline-flex items-center gap-2.5 text-base font-semibold text-ink" data-testid={`${testid}-label`}>
        <span aria-hidden="true" className={`h-2 w-7 rounded-full ${accent}`} />
        {label}
      </p>
      <span aria-hidden="true" className="h-px flex-1 bg-ink/10" />
    </div>
    <ol className="grid grid-cols-1 gap-6 md:grid-cols-3 md:gap-8 lg:mt-5">
      {steps.map((s, i) => (
        <li key={s.no}>
          <StepTrack index={i} />
          <h3 className="mt-3 font-serif text-3xl leading-tight text-ink">
            {s.title}
            {s.footnote && <sup className="ml-1 font-sans text-[13px] text-olive" aria-label="footnote 1">1</sup>}
          </h3>
          <p className="mt-1.5 text-base leading-relaxed text-olive">{s.body}</p>
        </li>
      ))}
    </ol>
    {footnote && (
      <p className="mt-5 text-[13px] leading-relaxed text-olive" data-testid="how-earmark-footnote">
        <sup className="mr-1">1</sup>
        {footnote}{" "}
        <Link to="/faq?group=donors" className="font-semibold text-ink underline-offset-2 hover:underline" data-testid="how-earmark-faq-link">
          More in the FAQs
        </Link>
      </p>
    )}
  </div>
);

const AUDIENCES = [
  { key: "student", label: "For students" },
  { key: "donor", label: "For donors" },
];

export const HowItWorks = () => {
  const [audience, setAudience] = useState("student");
  const show = (key) => (audience === key ? "block" : "hidden") + " lg:block";

  return (
    <section id="how" className="relative overflow-hidden bg-bone text-ink" data-testid="how-it-works">
      <div className="relative mx-auto max-w-7xl px-6 py-16 lg:px-8 lg:py-24">
        <FadeUp>
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-12 lg:items-end">
            <div className="lg:col-span-7">
              <NoteEyebrow accent="bg-tangerine">how it works</NoteEyebrow>
              <h2 className="mt-5 font-serif text-3xl lg:text-[3.5rem] leading-[1.02] tracking-tight">
                Two sides. <EmSwash>One gift.</EmSwash>
              </h2>
            </div>
            <p className="max-w-md text-base leading-relaxed text-olive lg:col-span-5">
              What a student does. What a donor does. Where they meet: one gift to My Rich Aunt, awarded as a grant.
            </p>
          </div>
        </FadeUp>

        {/* Phones: one row at a time. Desktop: both rows, phone alongside. */}
        <div className="mt-10 grid grid-cols-2 gap-1 rounded-full bg-ink/5 p-1 lg:hidden" role="tablist" aria-label="Choose your side" data-testid="how-audience-toggle">
          {AUDIENCES.map((a) => (
            <button
              key={a.key}
              type="button"
              role="tab"
              aria-selected={audience === a.key}
              onClick={() => setAudience(a.key)}
              data-testid={`how-audience-${a.key}`}
              className={`rounded-full px-4 py-2.5 text-base font-semibold transition-colors ${audience === a.key ? "bg-ink text-sand" : "text-ink/70 hover:text-ink"}`}
            >
              {a.label}
            </button>
          ))}
        </div>

        <div className="mt-10 grid grid-cols-1 gap-12 lg:mt-12 lg:grid-cols-12 lg:gap-x-12">
          <div className="space-y-12 lg:col-span-8">
            <FadeUp className={show("student")}>
              <Row label="For students" accent="bg-tangerine" steps={STEPS} testid="how-column-student" />
            </FadeUp>
            <FadeUp delay={0.1} className={show("donor")}>
              <Row label="For donors" accent="bg-cobalt" steps={DONOR_STEPS} footnote={FOOTNOTE} testid="how-column-donor" />
            </FadeUp>
          </div>
          <FadeUp delay={0.15} className="lg:col-span-4">
            <div className="origin-top lg:scale-[0.82] lg:[&>div]:mt-0">
              <SampleCampaignPreview />
            </div>
          </FadeUp>
        </div>
      </div>
    </section>
  );
};
